import { Popover } from 'radix-ui';
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';

import { useFieldControl } from './Field.js';
import { cn } from '../lib/cn.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * COMBOBOX — the filterable one. ARIA 1.2 editable combobox, hand-built.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `Select` is for a short, known list. This is for the ones a human would rather type at: four
 * hundred extensions, every DID on the account. Radix has no combobox, so the widget is built
 * here on `Popover` — which is used ONLY as a dismissable, positioned layer. Every ARIA
 * relationship below is this file's responsibility.
 *
 * ── THE ONE DECISION EVERYTHING ELSE FOLLOWS FROM: `aria-activedescendant` ─
 * There are two ways to make a list navigable from a text field. Roving `tabindex` moves real
 * DOM focus onto the option — and that BREAKS the widget, because the moment focus leaves the
 * `<input>` the user cannot type. Their next keystroke goes to the list, `value` stops
 * changing, and the filter appears to freeze. So focus never moves: the input keeps it for the
 * entire interaction, and `aria-activedescendant` points at the option the user has arrowed to.
 * Assistive technology reads the referenced option as if it were focused, which is exactly the
 * illusion wanted, and the caret stays where it is.
 *
 * That single decision is why:
 *   · the listbox's `onMouseDown` calls `preventDefault()` — a mousedown on a non-focusable
 *     element blurs the input in every browser, which would close the popup before the click
 *     that selects an option ever lands. This is the classic "options are unclickable" bug.
 *   · `Popover.Content` has its open- and close-autofocus prevented. Radix's default is to move
 *     focus into the layer, which is right for a dialog and fatal here.
 *   · ArrowUp/ArrowDown `preventDefault()`. Their native behaviour in a text field is to jump
 *     the caret to the start/end of the value, so without this the caret teleports every time
 *     the user moves down the list.
 *
 * ── ACTIVE IS NOT SELECTED ────────────────────────────────────────────────
 * `aria-activedescendant` is where the user is; `aria-selected` is what they have chosen. The
 * naive implementation puts `aria-selected="true"` on the active option, and then a screen
 * reader tells the user that every option they arrow past is selected — which is precisely the
 * information they were listening for. Only the chosen option is selected here, and the active
 * one is marked with `data-active` for the eye alone.
 *
 * The two therefore need two different visual treatments, and they get them: active is an
 * outline in the `ring` token (see the contrast argument on the option itself), chosen is a
 * check mark in the left gutter. One row can be both.
 *
 * ── WHY Home/End ARE NOT INTERCEPTED ──────────────────────────────────────
 * Both are listed as list-navigation keys in the listbox pattern and NOT in the editable
 * combobox pattern, because in an editable combobox they belong to the text: Home moves the
 * caret to the start of what the user typed. Stealing them makes the field stop behaving like
 * a text field, which is the thing a keyboard user is relying on.
 *
 * ── ESCAPE, AND WHY IT RESTORES RATHER THAN CLEARS ────────────────────────
 * Escape closes the popup and puts the input back to the chosen option's label. The typed
 * filter is scratch state, not data — it exists to find something. Discarding it on dismiss
 * means the field can never be left showing text that disagrees with the value underneath it,
 * which on a DID picker is the difference between "I set the route" and "I thought I did". The
 * same restore runs on blur and on an outside click, for the same reason.
 */

export interface ComboboxOption {
  value: string;
  label: string;
  /** Secondary line — "Reception", "Nanaimo, BC". Part of the accessible name and of the filter. */
  description?: string;
  disabled?: boolean;
}

export interface ComboboxProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'value' | 'defaultValue' | 'onSelect' | 'children' | 'type' | 'role'
  > {
  options: readonly ComboboxOption[];
  /** Controlled selection, by option value. `null` means nothing is chosen. */
  value?: string | null;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /**
   * Renders a visible `<label>`, wired to the input natively and to the popup listbox by id.
   * Omit it only if you pass `aria-label`/`aria-labelledby` yourself.
   */
  label?: string;
  /** Shown when the popup has no matches, and announced in the live region. */
  emptyMessage?: string;
  /** Override the match. Default is a case-insensitive substring of label + description. */
  filter?: (option: ComboboxOption, query: string) => boolean;
  invalid?: boolean;
  /** Class for the label+field wrapper. `className` itself lands on the `<input>`. */
  containerClassName?: string;
}

const defaultFilter = (option: ComboboxOption, query: string): boolean => {
  const haystack = `${option.label} ${option.description ?? ''}`.toLowerCase();
  return haystack.includes(query.trim().toLowerCase());
};

const ChevronIcon = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" fill="none" focusable="false" aria-hidden="true">
    <path d="m4 6 4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 16 16" width="14" height="14" fill="none" focusable="false" aria-hidden="true">
    <path d="m3.5 8.5 3 3 6-7" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const Combobox = forwardRef<HTMLInputElement, ComboboxProps>(function Combobox(
  {
    options,
    value,
    defaultValue,
    onValueChange,
    label,
    placeholder,
    emptyMessage = 'No matches',
    filter = defaultFilter,
    invalid = false,
    disabled = false,
    required = false,
    // Pulled off the visible input on purpose — see the hidden input near the bottom of the
    // render. `form` stays on both so an out-of-form combobox associates either way.
    name,
    form,
    containerClassName,
    className,
    id,
    onKeyDown,
    onBlur,
    onClick,
    onChange,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    ...rest
  },
  ref,
) {
  /**
   * ── COMPOSES WITH `Field`, ON THE SAME CONTRACT AS `Input` AND `Select` ────
   * `Select` shipped without this and produced a trigger with NO ACCESSIBLE NAME the first
   * time a page wrapped it in a `Field` — axe caught it as `critical` `button-name`. Three
   * form controls with one composition model is the fix; this is the third.
   *
   * Inside a Field the Field owns the visible label, the description and the invalid state,
   * and this renders none of its own. Outside one, everything below is unchanged.
   */
  const field = useFieldControl();

  const uid = useId();
  // The Field's id WINS: its `<label for>` was committed to that id before this rendered.
  const inputId = field?.id ?? id ?? `${uid}-input`;
  const labelId = `${uid}-label`;
  const listboxId = `${uid}-listbox`;
  const optionId = (index: number): string => `${uid}-option-${String(index)}`;

  const inputRef = useRef<HTMLInputElement | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  /**
   * The caller's ref and the internal one have to coexist: the internal one is what restores
   * focus after a selection, and dropping the caller's is the composition failure rule 1 exists
   * to prevent. Memoised because an inline callback ref is torn down and re-attached (called
   * with `null`, then the node) on every single render.
   */
  const setInputRef = useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref !== null) ref.current = node;
    },
    [ref],
  );

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  /**
   * `null` is not "empty" — it means "not filtering", i.e. show the selected option's label and
   * offer the WHOLE list. Modelling the filter as `string | null` rather than mirroring the
   * input's text into state is what makes reopening a combobox that already has a selection
   * show every option instead of just the one whose label happens to equal the current text.
   * It also removes the effect that would otherwise be needed to re-sync the text whenever a
   * controlled `value` changes from outside.
   */
  const [query, setQuery] = useState<string | null>(null);

  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState<string | null>(defaultValue ?? null);
  const selectedValue = isControlled ? value : internalValue;
  const selectedOption = options.find((option) => option.value === selectedValue) ?? null;

  const filtered = useMemo(
    () =>
      query === null || query.trim() === ''
        ? options
        : options.filter((option) => filter(option, query)),
    [options, query, filter],
  );

  const activeOption = activeIndex >= 0 ? (filtered[activeIndex] ?? null) : null;

  // The visible text: the filter while filtering, the committed label otherwise.
  const inputValue = query ?? selectedOption?.label ?? '';

  const close = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
    setQuery(null);
  }, []);

  /**
   * Routed through Radix so that ITS dismissals — outside pointerdown, Escape caught by the
   * layer, focus leaving — restore the input too. Handling those only in the input's own
   * handlers is how a component ends up with three ways to close and two of them leaking state.
   */
  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (next) setOpen(true);
      else close();
    },
    [close],
  );

  const commit = useCallback(
    (option: ComboboxOption) => {
      if (option.disabled === true) return;
      if (!isControlled) setInternalValue(option.value);
      onValueChange?.(option.value);
      close();
      // Belt and braces: the listbox already suppresses the focus-stealing mousedown, but a
      // selection must never be the thing that loses the user their place.
      inputRef.current?.focus();
    },
    [close, isControlled, onValueChange],
  );

  /** Steps over disabled options, and wraps — a short list should not dead-end at either edge. */
  const move = useCallback(
    (delta: 1 | -1) => {
      const selectable = filtered
        .map((option, index) => (option.disabled === true ? -1 : index))
        .filter((index) => index >= 0);
      if (selectable.length === 0) {
        setActiveIndex(-1);
        return;
      }
      const current = selectable.indexOf(activeIndex);
      const next =
        current === -1
          ? (delta === 1 ? selectable[0] : selectable[selectable.length - 1])
          : selectable[(current + delta + selectable.length) % selectable.length];
      setActiveIndex(next ?? -1);
    },
    [activeIndex, filtered],
  );

  /**
   * Keeping the active option in view is not decoration: with `aria-activedescendant` the
   * browser does NOT scroll for us the way it would for real focus, so a user arrowing through
   * four hundred extensions would be told about options that are off-screen.
   */
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const node = document.getElementById(`${uid}-option-${String(activeIndex)}`);
    node?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex, uid]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault(); // see header: otherwise the caret jumps to the end of the text
        if (!open) {
          setOpen(true);
          // Alt+Down is the "just show me the list" key: it opens WITHOUT choosing a position,
          // so nothing is announced as focused and Enter still does nothing.
          if (!event.altKey) move(1);
        } else if (!event.altKey) {
          move(1);
        }
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        if (event.altKey) {
          close();
        } else if (!open) {
          setOpen(true);
          move(-1);
        } else {
          move(-1);
        }
        break;
      }
      case 'Enter': {
        // Only swallowed when it actually does something. A combobox inside a form whose popup
        // is closed must still submit it — hijacking Enter unconditionally is how a form
        // becomes un-submittable from the keyboard.
        if (open && activeOption !== null) {
          event.preventDefault();
          commit(activeOption);
        }
        break;
      }
      case 'Escape': {
        if (open) {
          // `preventDefault` because in a form-associated text field Escape is a reset in some
          // engines, and dismissing a popup should never discard the field's value.
          event.preventDefault();
          close();
        }
        break;
      }
      case 'Tab': {
        // Deliberately NOT prevented: Tab means leave, and 2.1.2 says it has to work. The popup
        // is dismissed on the way out; `blur` restores the text.
        if (open) close();
        break;
      }
      default:
        break;
    }
    onKeyDown?.(event);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>): void => {
    close();
    onBlur?.(event);
  };

  const handleClick = (event: MouseEvent<HTMLInputElement>): void => {
    // Open, never toggle. A click on the input while the popup is open has already been seen by
    // Radix's dismissable layer, and a toggle here would fight it into a close/open flicker.
    setOpen(true);
    onClick?.(event);
  };

  const hasMatches = filtered.length > 0;

  const announcement = open
    ? hasMatches
      ? `${String(filtered.length)} ${filtered.length === 1 ? 'result' : 'results'} available`
      : emptyMessage
    : '';

  /**
   * The popup carries `role="listbox"` and needs a name of its own: `aria-controls` is a
   * pointer, not an inheritance, so the input's name does not reach it. The same label names
   * both, and the last-resort string exists because an unnamed popup is announced as a bare
   * "list box".
   *
   * axe will not catch a miss here — `aria-input-field-name` exempts a listbox that is a
   * combobox's popup — so the name is asserted directly in the test instead.
   */
  const ownsLabel = field === null && label !== undefined;
  /**
   * MERGED, not replaced — `Input`'s rule. A caller's own `aria-describedby` (a status line under
   * the field, a server's verdict) is information besides the Field's description and error, not
   * instead of them. It used to arrive in `...rest`, spread after this, and REPLACE them: the
   * format hint and the error left the accessibility tree the moment anyone described the field.
   */
  const describedBy = [field?.['aria-describedby'], ariaDescribedBy].filter(Boolean).join(' ') || undefined;
  const isInvalid = invalid || field?.['aria-invalid'] === true;

  /**
   * Inside a Field the name is `field.labelId`. Unlike a plain `<input>`, this cannot rely on
   * the native `<label for>` association alone: the listbox popup needs a name too, and
   * `aria-labelledby` is the only way to give it one — see the header's note on naming the
   * popup.
   */
  const nameRefs = [ariaLabelledBy, field?.labelId ?? (label === undefined ? undefined : labelId)].filter(
    (candidate): candidate is string => candidate !== undefined,
  );
  const listboxName =
    nameRefs.length > 0
      ? { 'aria-labelledby': nameRefs.join(' ') }
      : { 'aria-label': ariaLabel ?? 'Suggestions' };

  return (
    <div className={cn('flex flex-col gap-1.5', containerClassName)}>
      {!ownsLabel ? null : (
        <label id={labelId} htmlFor={inputId} className="text-sm font-medium text-fg">
          {label}
        </label>
      )}

      <Popover.Root open={open} onOpenChange={handleOpenChange}>
        {/* `Anchor`, not `Trigger`: a Trigger is a button that takes focus and toggles, and this
            widget has exactly one focusable node by design. The anchor only tells the popper
            what to position against. */}
        <Popover.Anchor asChild>
          <div ref={anchorRef} className="relative flex items-center">
            <input
              ref={setInputRef}
              id={inputId}
              type="text"
              role="combobox"
              aria-expanded={open}
              /**
               * `aria-controls` is set ONLY while the popup exists. A reference to an id that is
               * not in the document is a broken relationship, not a harmless one — axe reports
               * it as a violation once `aria-expanded` is true, and a screen reader following it
               * finds nothing to move into.
               *
               * `aria-haspopup` is deliberately absent: `role="combobox"` in ARIA 1.2 already
               * implies `listbox`, and stating it explicitly re-reads `aria-controls` as a
               * reference INTO a popup — a different, and unverifiable, claim.
               */
              {...(open ? { 'aria-controls': listboxId } : {})}
              {...(open && activeOption !== null
                ? { 'aria-activedescendant': optionId(activeIndex) }
                : {})}
              // `list`, not `both`/`inline`: nothing is written into the field on the user's
              // behalf, so promising inline completion would be a lie a screen reader repeats.
              aria-autocomplete="list"
              aria-invalid={isInvalid || undefined}
              {...(describedBy === undefined ? {} : { 'aria-describedby': describedBy })}
              {...(ariaLabel === undefined ? {} : { 'aria-label': ariaLabel })}
              /**
               * Left off when `label` is the only name — the native `<label for>` already does
               * that job and a second mechanism is a second thing to keep in sync.
               *
               * Set the moment the caller adds a name of their own, because BOTH of the ARIA
               * naming attributes outrank a `<label>` and would otherwise silently drop the
               * text the user can actually see. That is WCAG 2.5.3 Label in Name (Level A): a
               * speech-input user saying "Destination" must reach the field labelled
               * "Destination", which they cannot if its name has been replaced by
               * "Pick a DID". Referencing the visible label puts it back at the front. This
               * matches `Select`, where the same composition happens for the same reason.
               */
              {...(nameRefs.length > 0 && (ariaLabelledBy !== undefined || ariaLabel !== undefined)
                ? { 'aria-labelledby': nameRefs.join(' ') }
                : {})}
              // The browser's own autofill dropdown renders on top of the listbox and steals the
              // same arrow keys. There is no way to lose that race, so it is opted out of.
              autoComplete="off"
              spellCheck={false}
              disabled={disabled}
              required={required}
              form={form}
              {...(placeholder === undefined ? {} : { placeholder })}
              value={inputValue}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setQuery(event.target.value);
                // Every keystroke invalidates the position — nothing is active until the user
                // arrows to it. Auto-activating the first match would let Enter commit an option
                // the user has neither seen nor heard.
                setActiveIndex(-1);
                setOpen(true);
                onChange?.(event);
              }}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              onClick={handleClick}
              className={cn(
                'w-full rounded-md border bg-surface px-3 py-2 pr-9 text-sm text-fg',
                'min-h-10 transition-colors duration-150',
                'placeholder:text-fg-muted',
                'disabled:pointer-events-none disabled:opacity-50',
                isInvalid ? 'border-danger' : 'border-border-strong',
                className,
              )}
              {...rest}
            />
            {/* Decorative, and NOT a button: a second interactive node would add a tab stop that
                does nothing a keyboard user cannot already do with Alt+Down. */}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute right-3 text-fg-muted"
            >
              <ChevronIcon />
            </span>
          </div>
        </Popover.Anchor>

        <Popover.Portal>
          <Popover.Content
            /**
             * `role="listbox"` OVERRIDES Radix's `role="dialog"`, and it has to. A dialog
             * announces itself as one, expects a name, and expects focus — none of which is true
             * of a combobox popup. Radix spreads caller props after its own, so this wins.
             */
            role="listbox"
            id={listboxId}
            {...listboxName}
            // See the header: focus belongs to the input for the whole interaction.
            onOpenAutoFocus={(event) => {
              event.preventDefault();
            }}
            onCloseAutoFocus={(event) => {
              event.preventDefault();
            }}
            /**
             * The input is outside the layer, so clicking it counts as an outside pointerdown
             * and Radix would close on the way to our `onClick` reopening it. Excluding our own
             * anchor is what a `Popover.Trigger` would have done for us.
             */
            onPointerDownOutside={(event) => {
              if (anchorRef.current?.contains(event.target as Node) === true) {
                event.preventDefault();
              }
            }}
            onMouseDown={(event) => {
              // THE bug this widget is famous for. See the header.
              event.preventDefault();
            }}
            align="start"
            sideOffset={4}
            className={cn(
              'z-popover max-h-72 w-[var(--radix-popover-trigger-width)] overflow-y-auto p-1',
              'rounded-md border border-border bg-surface-raised text-fg shadow-lg',
            )}
          >
            {hasMatches ? (
              filtered.map((option, index) => (
                <div
                  key={option.value}
                  id={optionId(index)}
                  role="option"
                  /* Chosen, not merely arrowed to. See "ACTIVE IS NOT SELECTED". */
                  aria-selected={option.value === selectedValue}
                  {...(option.disabled === true ? { 'aria-disabled': true } : {})}
                  data-active={index === activeIndex ? '' : undefined}
                  onClick={() => {
                    commit(option);
                  }}
                  // Hover moves the active option so the pointer and the keyboard cannot
                  // disagree about which row Enter would take.
                  onPointerMove={() => {
                    if (option.disabled !== true) setActiveIndex(index);
                  }}
                  className={cn(
                    'relative cursor-default rounded-sm py-1.5 pr-2 pl-8 text-sm select-none',
                    /**
                     * THE TINT IS NOT THE INDICATOR, THE OUTLINE IS — and that is a contrast
                     * requirement, not a preference. `accent-subtle` is 1.15:1 against
                     * `surface-raised` in light and 1.11:1 in dark: it is a wash, deliberately,
                     * because its job elsewhere is to sit BEHIND accent-coloured text at 4.5:1.
                     *
                     * `Select` can get away with the tint alone because Radix moves real DOM
                     * focus onto the highlighted item and the global `:focus-visible` ring is
                     * drawn on top of it. Nothing here does: with `aria-activedescendant` the
                     * input keeps focus, so the browser draws no ring in the popup and this
                     * highlight is the ONLY visual statement of where the keyboard is. At
                     * 1.15:1 that fails 1.4.11 (3:1 for a state indicator) and leaves 2.4.7
                     * with nothing to be visible. `ring` is the token held to 3:1 against every
                     * surface — 6.1:1 light, 6.9:1 dark — so it draws the indicator and the
                     * tint stays as the soft fill it is good at.
                     *
                     * The offset is NEGATIVE so the outline is painted inside the row: the
                     * popup is `overflow-y-auto`, which clips anything drawn outside the first
                     * and last child.
                     */
                    'data-[active]:bg-accent-subtle',
                    'data-[active]:outline-2 data-[active]:-outline-offset-2 data-[active]:outline-ring',
                    option.disabled === true ? 'pointer-events-none opacity-50' : '',
                  )}
                >
                  {/* The chosen row is marked to the EYE as well as to `aria-selected`, the same
                      way `Select` does it. Without this a user reopening a 400-row DID picker
                      can hear which one is set and cannot see it. `aria-hidden` because
                      `aria-selected` already says it — a spoken "tick" on top is noise. */}
                  {option.value === selectedValue ? (
                    <span
                      aria-hidden="true"
                      className="absolute left-2 inline-flex items-center text-accent"
                    >
                      <CheckIcon />
                    </span>
                  ) : null}
                  {option.label}
                  {option.description === undefined ? null : (
                    <span className="block text-xs text-fg-muted">{option.description}</span>
                  )}
                </div>
              ))
            ) : (
              /**
               * A message shaped like a disabled option rather than a bare `<div>`: a
               * `role="listbox"` with no owned options is an invalid — and, to a screen reader,
               * silent — popup. It is never active and never committable.
               */
              <div
                role="option"
                aria-disabled="true"
                aria-selected="false"
                /* Same left gutter as a real row, so the message does not jump when a keystroke
                   turns the empty state back into a list. */
                className="py-1.5 pr-2 pl-8 text-sm text-fg-muted"
              >
                {emptyMessage}
              </div>
            )}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {/**
       * WHAT A FORM ACTUALLY SUBMITS, AND WHY IT IS NOT THE VISIBLE INPUT.
       *
       * The `<input>` above holds the option's LABEL, because that is what a human reads while
       * they type. `name` on it would therefore submit "+1 250 555 0101" for a DID whose value
       * is "12505550101" — a form that posts successfully and posts the wrong thing, which is
       * the worst available failure. `name` is moved here instead, onto the option's value,
       * which is the same separation Radix gives `Select` through its hidden native `<select>`.
       */}
      {name === undefined ? null : (
        <input type="hidden" name={name} form={form} value={selectedValue ?? ''} />
      )}

      {/**
       * The result count, announced politely. This is the only channel through which a
       * screen-reader user learns that typing another character narrowed 40 rows to 2 — the
       * listbox re-rendering is silent to them. `role="status"` is implicitly `aria-live
       * polite`, and the node is mounted at all times because a live region that appears at the
       * same moment as its text is not announced by most screen readers.
       */}
      <div role="status" className="sr-only-focusable absolute">
        {announcement}
      </div>
    </div>
  );
});
